'use client';

import { useState } from 'react';
import type { ContentFile } from '@eliadmin/core/src/api/content-fields/content-fields-types';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { imageSource, resolveImageAlt, toLightboxSlides } from '@eliadmin/core/src/utils/image-alt-utils';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

export function ProductContentGallery({ images, name }: { images: ContentFile[]; name: string }) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const visibleImages = images.filter(image => imageSource(image));
  if (!visibleImages.length) return null;
  const selectedIndex = visibleImages.findIndex(image => image.id === selectedId);

  return (
    <div className="ea-content-fields__gallery w-full">
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {visibleImages.slice(0, 3).map((image, index) => {
          const remaining = index === 2 && visibleImages.length > 3 ? visibleImages.length - 2 : 0;
          return (
            <button
              key={image.id}
              type="button"
              aria-label={remaining ? `Zobraziť ďalšie obrázky (${remaining})` : resolveImageAlt(image, name)}
              aria-haspopup="dialog"
              onClick={() => setSelectedId(image.id)}
              className="group relative aspect-[4/3] overflow-hidden rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:rounded-2xl"
            >
              <Image
                src={imageSource(image)!}
                alt={image.is_decorative ? '' : resolveImageAlt(image, name)}
                fill
                sizes="(min-width: 1280px) 400px, 33vw"
                className="object-cover transition-opacity group-hover:opacity-90"
              />
              {remaining > 0 && (
                <span aria-hidden="true" className="absolute inset-0 flex items-center justify-center bg-primary/80 text-2xl font-semibold text-primary-foreground sm:text-4xl">
                  +{remaining}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selectedIndex >= 0 && (
        <Lightbox
          open
          close={() => setSelectedId(null)}
          index={selectedIndex}
          slides={toLightboxSlides(visibleImages, name)}
          plugins={[Thumbnails, Zoom]}
          styles={{ root: { zIndex: 999999 }, container: { zIndex: 999999 } }}
        />
      )}
    </div>
  );
}
